import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetPageById } from '@tinct/core-api';
import {
  EmptyChartContent,
  LineChartCard,
  MetricCard,
} from '@tinct/web-components';
import type {
  LineChartCardDataPoint,
  LineChartCardSeries,
} from '@tinct/web-components';
import { LineChart as LineChartIcon } from 'lucide-react';
import {
  ComparisonBadge,
  ComparisonBadges,
} from '../../shared';

const CHART_SERIES: LineChartCardSeries[] = [
  { key: 'personalized', label: 'Personalized', color: 'var(--color-chart-1, #6366f1)' },
];

const trendOf = (value: number | null | undefined): 'up' | 'down' =>
  value !== null && value !== undefined && value >= 0 ? 'up' : 'down';

const badgeOf = (
  value: number | null | undefined,
  label: string,
): ComparisonBadge | null =>
  value !== null && value !== undefined
    ? { value: `${Math.abs(Math.round(value))}%`, label, trend: trendOf(value) }
    : null;

export function VariantAnalyticsTab() {
  const { variantId = '' } = useParams<{ id: string; variantId: string }>();

  const periodParams = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      periodStart: `"${start.toISOString().split('T')[0]}"` as `"${number}-${number}-${number}"`,
      periodEnd: `"${now.toISOString().split('T')[0]}"` as `"${number}-${number}-${number}"`,
    };
  }, []);

  const { data: page } = useGetPageById({
    id: variantId,
    fields: {
      id: null,
      statistics: {
        fields: {
          visitsCount: null,
          periodVisitsVariationInPercent: { temporalPeriod: '"1w"' },
          sessionAverageTimeInSeconds: null,
          periodSessionAverageTimeVariationInPercent: { temporalPeriod: '"1w"' },
          ctaClickPerSession: null,
          periodCtaClickPerSessionVariationInPercent: { temporalPeriod: '"1w"' },
          visitsTimeSeries: null,
        },
        ...periodParams,
      },
    },
  });

  const stats = page?.statistics;

  const visitsCount = stats?.visitsCount ?? 0;
  const visitsBadges = [
    badgeOf(stats?.periodVisitsVariationInPercent, 'vs last week'),
  ].filter(Boolean) as ComparisonBadge[];

  const avgTimeSec = stats?.sessionAverageTimeInSeconds;
  const avgTimeLabel =
    avgTimeSec !== null && avgTimeSec !== undefined
      ? avgTimeSec >= 60
        ? `${Math.floor(avgTimeSec / 60)}m ${Math.round(avgTimeSec % 60)}s`
        : `${Math.round(avgTimeSec)}s`
      : '—';
  const avgTimeBadges = [
    badgeOf(stats?.periodSessionAverageTimeVariationInPercent, 'vs last week'),
  ].filter(Boolean) as ComparisonBadge[];

  const ctaClickPerSession = stats?.ctaClickPerSession;
  const ctaLabel =
    ctaClickPerSession !== null && ctaClickPerSession !== undefined
      ? `${Math.round(ctaClickPerSession * 10) / 10}/session`
      : '—';
  const ctaBadges = [
    badgeOf(stats?.periodCtaClickPerSessionVariationInPercent, 'vs last week'),
  ].filter(Boolean) as ComparisonBadge[];

  const chartData = useMemo<LineChartCardDataPoint[]>(() => {
    return (stats?.visitsTimeSeries?.series ?? [])
      .filter((point) => point.date)
      .map((point) => ({
        date: point.date!.split('T')[0],
        personalized: point.value ?? 0,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [stats?.visitsTimeSeries]);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricCard
          headerText="Visits"
          statisticText={`${visitsCount}`}
          slot={visitsBadges.length > 0 ? <ComparisonBadges badges={visitsBadges} /> : undefined}
        />
        <MetricCard
          headerText="Average time"
          statisticText={avgTimeLabel}
          slot={avgTimeBadges.length > 0 ? <ComparisonBadges badges={avgTimeBadges} /> : undefined}
        />
        <MetricCard
          headerText="CTA Clicks"
          statisticText={ctaLabel}
          slot={ctaBadges.length > 0 ? <ComparisonBadges badges={ctaBadges} /> : undefined}
        />
      </div>

      <LineChartCard
        headerIcon={<LineChartIcon className="size-4" />}
        headerLabel="Chart"
        title="Visits over time"
        series={CHART_SERIES}
        data={chartData}
        emptyState={
          <EmptyChartContent
            title="No data yet"
            description="Your statistics, performance, and insights will be displayed here once this page variant is active."
          />
        }
      />
    </div>
  );
}
